import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Divider } from '@mui/material';
import MicrosoftLogo from '../components/MicrosoftLogo';
import AuthLandingLayout from '../components/AuthLandingLayout';
import AuthCustomerHeading from '../components/AuthCustomerHeading';
import { useSelectedCustomerName } from '../hooks/useSelectedCustomerName';
import { openAccessRequestMail } from '../lib/accessRequestMail';
import {
  clearSelectedCustomerId,
  getPortalSelectUrl,
  getSelectedCustomer,
  getSelectedCustomerId,
  setSelectedCustomerId
} from '../lib/selectedCustomer';
import {
  isLocalhost,
  navigateTo,
  redirectTo,
  resolveCustomerAppUrl,
  shouldEnforceCustomerHostname
} from '../lib/runtimeUrls';
import { getHostnameFromAppUrl } from '../config/customers';
import { getResolvedCustomerId } from '../config/environment';
import { getCustomerFromCurrentHostname } from '../lib/customerFromHostname';
import { useThemeMode } from '../theme/ThemeModeContext';
import { accent, darkTokens, lightTokens } from '../theme/tokens';

const CustomerLandingPage = ({ onLogin }) => {
  const [loggingIn, setLoggingIn] = useState(false);
  const { mode } = useThemeMode();
  const tokens = mode === 'dark' ? darkTokens : lightTokens;
  const customerName = useSelectedCustomerName({ redirectIfMissing: true });

  useEffect(() => {
    const fromHostname = getCustomerFromCurrentHostname();
    if (fromHostname?.id && fromHostname.id !== getSelectedCustomerId()) {
      setSelectedCustomerId(fromHostname.id);
      return;
    }

    const selected = getSelectedCustomer();

    if (selected && shouldEnforceCustomerHostname()) {
      const expectedHost = getHostnameFromAppUrl(selected.appUrl);
      if (expectedHost && expectedHost !== window.location.hostname) {
        redirectTo(resolveCustomerAppUrl(selected));
      }
    }
  }, []);

  const handleLogin = async () => {
    setLoggingIn(true);
    try {
      await onLogin();
    } catch (error) {
      console.error('Login error:', error);
      setLoggingIn(false);
    }
  };

  const handleChangeCustomer = () => {
    clearSelectedCustomerId();

    // Local portal (:3000) is often another project — avoid redirect hang.
    if (isLocalhost()) {
      const fallback = getResolvedCustomerId();
      if (fallback) {
        setSelectedCustomerId(fallback);
        window.location.assign(`/?k=${encodeURIComponent(fallback)}`);
        return;
      }
    }

    navigateTo(getPortalSelectUrl());
  };

  const handleRequestAccess = () => {
    openAccessRequestMail({ customerName });
  };

  if (!getSelectedCustomerId()) {
    return (
      <AuthLandingLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </AuthLandingLayout>
    );
  }

  return (
    <AuthLandingLayout>
      <AuthCustomerHeading customerName={customerName} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          size="large"
          disabled={loggingIn}
          onClick={handleLogin}
          sx={{
            py: 1.6,
            fontSize: '1.05rem',
            gap: 1.25,
            boxShadow: `0 12px 28px -8px ${accent.soft}`
          }}
        >
          {loggingIn ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>
              <MicrosoftLogo size={22} />
              Logg inn med Microsoft
            </>
          )}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          size="large"
          onClick={handleRequestAccess}
          sx={{ py: 1.5 }}
        >
          Be om tilgang
        </Button>
      </Box>

      <Divider sx={{ my: 3, borderColor: tokens.border }} />

      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="text"
          color="inherit"
          onClick={handleChangeCustomer}
          sx={{
            fontWeight: 600,
            color: tokens.ink70,
            '&:hover': { color: accent.main, bgcolor: accent.soft }
          }}
        >
          Bytt kommune
        </Button>
      </Box>
    </AuthLandingLayout>
  );
};

export default CustomerLandingPage;
